import { useEffect, useMemo, useState } from 'react'
import { Calendar } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { startOfMonth } from 'date-fns'
import type { Deal } from '@/types'
import { DealsCalendarGrid } from './DealsCalendarGrid'
import { DealsCalendarDayList } from './DealsCalendarDayList'
import {
  buildDealsByDayKey,
  dealsOnDay,
  pickInitialSelectedDay,
} from './dealsCalendarUtils'

interface DealsCalendarViewProps {
  visibleDeals: Deal[]
  onSelectDeal: (deal: Deal) => void
}

export function DealsCalendarView({ visibleDeals, onSelectDeal }: DealsCalendarViewProps) {
  const dealsByDay = useMemo(() => buildDealsByDayKey(visibleDeals), [visibleDeals])

  const dealCountByDay = useMemo(() => {
    const counts = new Map<string, number>()
    for (const [key, list] of dealsByDay) counts.set(key, list.length)
    return counts
  }, [dealsByDay])

  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()))
  const [selectedDay, setSelectedDay] = useState<Date | null>(null)

  useEffect(() => {
    if (selectedDay) return
    const initial = pickInitialSelectedDay(dealsByDay, viewMonth)
    if (initial) {
      setSelectedDay(initial)
      setViewMonth(startOfMonth(initial))
    }
  }, [dealsByDay, selectedDay, viewMonth])

  const handleSelectDay = (day: Date) => {
    setSelectedDay(day)
    setViewMonth(startOfMonth(day))
  }

  const selectedDeals = useMemo(
    () => dealsOnDay(dealsByDay, selectedDay),
    [dealsByDay, selectedDay],
  )

  const dayHeading = selectedDay
    ? format(selectedDay, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    : ''

  if (dealsByDay.size === 0) {
    return (
      <div className='glass-card p-12 text-center'>
        <Calendar size={40} className='mx-auto mb-3 text-text-label opacity-40' />
        <p className='text-sm text-text-label'>Nenhum deal com data agendada</p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <DealsCalendarGrid
        viewMonth={viewMonth}
        onViewMonthChange={setViewMonth}
        selectedDay={selectedDay}
        onSelectDay={handleSelectDay}
        dealCountByDay={dealCountByDay}
      />
      <DealsCalendarDayList
        heading={dayHeading}
        deals={selectedDeals}
        onSelectDeal={onSelectDeal}
      />
    </div>
  )
}
